import React, { useMemo, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useFamilyData } from "../../hooks/useFamilyData";
import { logAudit } from "../../utils/audit";
import { createActionId, executeAction } from "../../utils/actions";
import { runActionWithQueue } from "../../utils/actionQueue";

export default function ClarificationsCard({ familyId, canManage, clarificationsEnabled }) {
  const { tasks = [] } = useFamilyData(familyId);
  const showClarifications = clarificationsEnabled !== false;
  const [drafts, setDrafts] = useState({});

  const getClarifications = (task) => {
    const clarifications = Array.isArray(task?.clarifications) ? task.clarifications : [];
    const getMs = (value) => {
      if (!value) return 0;
      if (value.toDate) return value.toDate().getTime();
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return [...clarifications].sort((a, b) => getMs(a.createdAt) - getMs(b.createdAt));
  };

  const formatClarificationTime = (value) => {
    if (!value) return "";
    if (value.toDate) return value.toDate().toLocaleString();
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
  };

  const clarificationTasks = useMemo(() => {
    if (!showClarifications) return [];
    return tasks.filter(
      (task) => task.needsClarification || (Array.isArray(task.clarifications) && task.clarifications.length > 0)
    );
  }, [tasks, showClarifications]);

  const sendReply = async (task) => {
    if (!canManage || !showClarifications) return;
    const message = drafts[task.id];
    if (!message || !message.trim()) return;
    const actionId = createActionId();
    const { queued } = await runActionWithQueue(
      {
        type: "task.clarify",
        familyId,
        taskId: task.id,
        actionId,
        message,
        authorRole: "parent",
        authorName: "Parent"
      },
      executeAction
    );
    if (!queued) {
      await logAudit({
        familyId,
        action: "clarify",
        entity: "task",
        meta: { taskId: task.id, kidId: task.assignedTo }
      });
    }
    setDrafts((prev) => ({ ...prev, [task.id]: "" }));
  };

  const clearThread = async (task) => {
    if (!canManage) return;
    await updateDoc(doc(db, "families", familyId, "tasks", task.id), {
      needsClarification: false,
      clarifications: [],
      lastClarificationAt: null
    });
    await logAudit({
      familyId,
      action: "clarify_clear",
      entity: "task",
      meta: { taskId: task.id, kidId: task.assignedTo }
    });
  };

  return (
    <div className="glass parent-card clarifications-card" data-tour="parent-clarifications-card">
      <div className="header-flex">
        <h2>Clarifications</h2>
        {clarificationTasks.length > 0 && (
          <span className="badge">{clarificationTasks.length} Threads</span>
        )}
      </div>
      <div className="card-body">
        {!showClarifications ? (
          <p style={{ textAlign: "center", opacity: 0.6 }}>
            Clarification messages are disabled in settings.
          </p>
        ) : clarificationTasks.length === 0 ? (
          <div style={{ textAlign: "center", opacity: 0.75, display: "grid", gap: 8 }}>
            <p style={{ margin: 0 }}>No clarification threads right now.</p>
            <button
              className="btn-nav"
              type="button"
              onClick={() =>
                document.querySelector("[data-tour='parent-approvals-card']")?.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            >
              Review approvals
            </button>
          </div>
        ) : !canManage ? (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        ) : (
          clarificationTasks.map((task) => (
            <div key={`clarify-${task.id}`} className="card clarification-item">
              <div className="clarification-task-meta">
                <strong>{task.title}</strong>
                <div style={{ fontSize: "0.85rem", opacity: 0.8 }}>
                  👤 {task.kidName || task.assignedToName || "Unknown"}
                </div>
              </div>
              <div className="clarification-thread">
                {getClarifications(task).map((note, idx) => (
                  <div key={note.id || `${task.id}-thread-${idx}`} className="clarification-bubble">
                    <div className="clarification-meta">
                      <strong>
                        {note.authorRole === "kid" ? note.authorName || "Kid" : "Parent"}
                      </strong>
                      <span>{formatClarificationTime(note.createdAt)}</span>
                    </div>
                    <div>{note.message}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: "10px", display: "grid", gap: "8px" }}>
                <textarea
                  placeholder="Reply to clarify..."
                  value={drafts[task.id] || ""}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [task.id]: e.target.value }))}
                  rows={2}
                />
                <div className="clarification-actions">
                  <button className="btn-nav" onClick={() => sendReply(task)}>
                    Send reply
                  </button>
                  <button className="btn-nav ghost" onClick={() => clearThread(task)}>
                    Clear thread
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
